import React, { createContext, useContext } from 'react';

/**
 * Building blocks for the booking page's cards.
 *
 * Each Card root carries `booking-detail-page`, the scope for camperDetail.css's resets. It goes on
 * each card rather than the page root so nothing else rendered in the page (the site header,
 * Bootstrap modals) is ever caught by the resets.
 */
export const Card = ({ id, title, icon: Icon, actions, children, className = '' }) => (
    <section id={id} className={`booking-detail-page tw-scroll-mt-40 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white ${className}`}>
        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3 tw-px-5 tw-pb-3 tw-pt-4">
            <h2 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wider tw-text-neutral-700">
                {Icon ? <Icon className="tw-h-4 tw-w-4 tw-text-neutral-500" aria-hidden="true" /> : null}
                {title}
            </h2>
            {actions}
        </div>
        <div className="tw-px-5 tw-pb-4">{children}</div>
    </section>
);

/** Two cards side by side (Participant + Guardian), stacked on narrow screens. */
export const CardRow = ({ children }) => (
    <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2">{children}</div>
);

const TONES = {
    default: 'tw-text-neutral-800',
    success: 'tw-text-green-700',
    warning: 'tw-text-amber-600',
    danger: 'tw-text-red-600',
};

const isEmpty = value => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

/**
 * The sidebar's "Show empty fields" switch. When false, a Field with no value renders nothing.
 * Defaults to true so a Field used outside the page's provider behaves as before.
 */
export const ShowEmptyFieldsContext = createContext(true);

/**
 * Label + value. An empty value renders as a muted `placeholder` ("Not provided") rather than "N/A"
 * or a blank. `aside` sits to the right of the value (copy button, toggle).
 */
export const Field = ({ icon: Icon, label, children, tone = 'default', aside, placeholder = 'Not provided', className = '' }) => {
    const showEmpty = useContext(ShowEmptyFieldsContext);
    const empty = isEmpty(children);
    if (empty && !showEmpty) return null;
    return (
        <div className={`tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5 ${className}`}>
            <div className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-400">
                {Icon ? <Icon className="tw-h-3 tw-w-3 tw-shrink-0" aria-hidden="true" /> : null}
                {label}
            </div>
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <div className={`tw-min-w-0 tw-break-words tw-text-sm ${empty ? 'tw-italic tw-text-neutral-400' : TONES[tone]}`}>
                    {empty ? placeholder : children}
                </div>
                {aside}
            </div>
        </div>
    );
};

/**
 * Grid of Fields. With empty fields hidden and every Field in it empty, shows one muted line
 * instead of a blank gap (or a sub-heading with nothing under it). Anything that isn't a Field,
 * such as a button, counts as content.
 */
export const FieldGrid = ({ className = '', children }) => {
    const showEmpty = useContext(ShowEmptyFieldsContext);
    const items = React.Children.toArray(children);
    const hasContent = items.some(child => child.type !== Field || !isEmpty(child.props.children));
    if (!showEmpty && !hasContent) {
        return <p className="tw-text-sm tw-italic tw-text-neutral-400">No details provided.</p>;
    }
    return <div className={className}>{children}</div>;
};
